/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Semantic Models List
 * Feature #197: Portal Light-Mode Theme, Modals & Responsive UX
 * Constitution: v1.20.0
 * Conformed to Spec 197 design blueprint
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Database } from 'lucide-react';
import { useSemanticModelsList } from '../../services/operations/metadataService';
import type { SmListItem } from '../../services/operations/metadataService';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';

const columns: DataTableColumn<SmListItem>[] = [
  { key: 'displayName', header: 'Model', width: '35%' },
  { key: 'workspaceName', header: 'Workspace', width: '30%' },
  { key: 'tableCount', header: 'Tables', width: '15%', align: 'right' },
  { key: 'measureCount', header: 'Measures', width: '20%', align: 'right' },
];

export const SemanticModelsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useSemanticModelsList();

  return (
    <PageLayout
      helpId="auto.semantic-models-list"
      title="Semantic Model Definitions"
      description="Semantic models with collected definitions (tables, columns, measures)"
      icon={<Database className="h-6 w-6" />}
      actions={
        <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/metadata')}>
          Metadata
        </Button>
      }
    >
      {isLoading ? (
        <LoadingState rows={6} />
      ) : isError ? (
        <ErrorState
          title="Failed to load semantic models"
          message="Unable to retrieve semantic model list. Please try again."
        />
      ) : !data?.length ? (
        <EmptyState
          title="No definitions collected"
          message="No semantic models with definition data have been collected yet. Run the daily collection job to populate this list."
        />
      ) : (
        <Card padding="none">
          <DataTable
            columns={columns}
            rows={data}
            rowKey={(r) => r.id}
            onRowClick={(r) => navigate(`/metadata/semantic-models/${r.id}`)}
            emptyMessage="No semantic models with definitions collected yet."
          />
        </Card>
      )}
    </PageLayout>
  );
};
